import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Upload, FileText, CheckCircle2, Archive, RefreshCw, Eye, Pencil, UploadCloud, BookOpen, AlertCircle, XCircle, CheckCircle } from "lucide-react";

type Doc = {
  id: string;
  title: string;
  organisation: string;
  jurisdiction: string;
  evidence_level: string;
  version: string | null;
  status: string;
  source_url: string | null;
  storage_path: string;
  published_at: string | null;
  reviewed_at: string;
  next_review_at: string;
  created_at: string;
  chunk_count: number;
  document_type: string | null;
  country: string | null;
  last_checked_date: string | null;
  source_version: string | null;
  copyright_status: string | null;
  permission_status: string | null;
  usage_restrictions: string | null;
  approved_for_rag: boolean | null;
  approved_for_quotation: boolean | null;
  approved_for_reproduction: boolean | null;
  reviewer_name: string | null;
  review_notes: string | null;
};

type Chunk = {
  id: string;
  document_id: string;
  chunk_index: number;
  content: string;
  heading_path: string | null;
  page_start: number | null;
  page_end: number | null;
  excluded: boolean;
  token_count: number | null;
};

type DbDoc = Omit<Doc, "chunk_count">;
type DocUpdate = Partial<DbDoc>;

const COPYRIGHT_STATUSES = ["unknown", "public_domain", "crown_copyright", "creative_commons", "proprietary", "permission_granted"];
const PERMISSION_STATUSES = ["not_checked", "not_required", "requested", "granted", "denied"];
const DOC_TYPES = ["guideline", "position_statement", "consumer_leaflet", "systematic_review", "regulatory", "other"];

const ORGS = ["NICE", "NAMS", "RANZCOG", "AMS", "Jean Hailes"];
const JURIS = ["AU", "UK", "US", "INT"];
const LEVELS = ["guideline", "position_statement", "consumer_education"];

const emptyForm = {
  title: "",
  organisation: ORGS[0],
  jurisdiction: "AU",
  evidence_level: "guideline",
  version: "",
  source_url: "",
  published_at: "",
  file: null as File | null,
};

export default function MedicalKnowledgeAdmin() {
  const { toast } = useToast();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [ingestingId, setIngestingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const [editDoc, setEditDoc] = useState<DbDoc | null>(null);
  const [editForm, setEditForm] = useState<DocUpdate>({});

  const [reviewDoc, setReviewDoc] = useState<Doc | null>(null);
  const [chunks, setChunks] = useState<Chunk[]>([]);
  const [chunksLoading, setChunksLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("medical_documents")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Load error", description: error.message, variant: "destructive" });
      setDocs([]);
    } else {
      const rows = (data ?? []) as DbDoc[];
      const counts: Record<string, number> = {};
      if (rows.length > 0) {
        const ids = rows.map((r) => r.id);
        const { data: chunkRows } = await supabase
          .from("medical_chunks")
          .select("document_id")
          .in("document_id", ids);
        (chunkRows ?? []).forEach((c) => {
          counts[c.document_id] = (counts[c.document_id] ?? 0) + 1;
        });
      }
      setDocs(rows.map((r) => ({ ...r, chunk_count: counts[r.id] ?? 0 })) as Doc[]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async () => {
    if (!form.file || !form.title) {
      toast({ title: "Missing fields", description: "Title and PDF required", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const path = `${Date.now()}-${form.file.name.replace(/[^a-z0-9.-]/gi, "_")}`;
      const { error: upErr } = await supabase.storage
        .from("medical-documents")
        .upload(path, form.file, { contentType: "application/pdf" });
      if (upErr) throw upErr;

      const { data: user } = await supabase.auth.getUser();
      const { data: doc, error: insErr } = await supabase.from("medical_documents").insert({
        title: form.title,
        organisation: form.organisation,
        jurisdiction: form.jurisdiction,
        evidence_level: form.evidence_level,
        version: form.version || null,
        source_url: form.source_url || null,
        published_at: form.published_at || null,
        storage_path: path,
        uploaded_by: user.user?.id,
        status: "draft",
      }).select().single();
      if (insErr) throw insErr;

      toast({ title: "Uploaded", description: "Now click Ingest to parse and embed." });
      setForm(emptyForm);
      await load();
    } catch (e) {
      toast({ title: "Upload failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const ingest = async (id: string) => {
    setIngestingId(id);
    try {
      const { data, error } = await supabase.functions.invoke("ingest-medical-document", {
        body: { document_id: id },
      });
      if (error) throw error;
      toast({ title: "Ingest complete", description: `Inserted ${data?.inserted ?? 0} chunks across ${data?.total_pages ?? 0} pages.` });
      await load();
    } catch (e) {
      toast({ title: "Ingest failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setIngestingId(null);
    }
  };

  const setStatus = async (id: string, status: string) => {
    const updates: DocUpdate = { status };
    if (status === "approved") {
      updates.reviewed_at = new Date().toISOString().split("T")[0];
    }
    const { error } = await supabase.from("medical_documents").update(updates).eq("id", id);
    if (error) toast({ title: "Update failed", description: error.message, variant: "destructive" });
    else await load();
  };

  const openEdit = (d: Doc) => {
    const { chunk_count: _, ...rest } = d;
    setEditDoc(rest as DbDoc);
    setEditForm(rest);
  };

  const saveEdit = async () => {
    if (!editDoc) return;
    const { error } = await supabase.from("medical_documents").update(editForm).eq("id", editDoc.id);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Metadata updated" });
      setEditDoc(null);
      await load();
    }
  };

  const openReview = async (d: Doc) => {
    setReviewDoc(d);
    setChunksLoading(true);
    const { data, error } = await supabase
      .from("medical_chunks")
      .select("*")
      .eq("document_id", d.id)
      .order("chunk_index", { ascending: true });
    if (error) toast({ title: "Chunks error", description: error.message, variant: "destructive" });
    setChunks((data ?? []) as Chunk[]);
    setChunksLoading(false);
  };

  const toggleExcluded = async (chunk: Chunk) => {
    const next = !chunk.excluded;
    const { error } = await supabase.from("medical_chunks").update({ excluded: next }).eq("id", chunk.id);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    } else {
      setChunks((prev) => prev.map((c) => (c.id === chunk.id ? { ...c, excluded: next } : c)));
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Upload className="h-5 w-5" /> Upload New Guideline</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. NICE NG23: Menopause Diagnosis and Management" />
            </div>
            <div>
              <Label>Organisation</Label>
              <Select value={form.organisation} onValueChange={(v) => setForm({ ...form, organisation: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ORGS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jurisdiction</Label>
              <Select value={form.jurisdiction} onValueChange={(v) => setForm({ ...form, jurisdiction: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{JURIS.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Evidence Level</Label>
              <Select value={form.evidence_level} onValueChange={(v) => setForm({ ...form, evidence_level: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Version</Label>
              <Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="e.g. 2024.11" />
            </div>
            <div>
              <Label>Published Date</Label>
              <Input type="date" value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Source URL</Label>
              <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="md:col-span-2">
              <Label>PDF File</Label>
              <Input type="file" accept="application/pdf" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] ?? null })} />
            </div>
          </div>
          <Button onClick={handleUpload} disabled={uploading}>
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Upload
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Documents</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : docs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 border rounded-lg bg-card">
                  <div className="text-xs text-muted-foreground">Total documents</div>
                  <div className="text-2xl font-semibold">{docs.length}</div>
                </div>
                <div className="p-3 border rounded-lg bg-primary text-primary-foreground">
                  <div className="text-xs text-primary-foreground/80">Approved / live</div>
                  <div className="text-2xl font-semibold">{docs.filter((d) => d.status === "approved").length}</div>
                </div>
                <div className="p-3 border rounded-lg bg-secondary text-secondary-foreground">
                  <div className="text-xs text-secondary-foreground/80">Ready for review</div>
                  <div className="text-2xl font-semibold">{docs.filter((d) => d.status === "draft" && d.chunk_count > 0).length}</div>
                </div>
                <div className="p-3 border rounded-lg bg-destructive text-destructive-foreground">
                  <div className="text-xs text-destructive-foreground/80">Not ingested</div>
                  <div className="text-2xl font-semibold">{docs.filter((d) => d.status !== "archived" && d.chunk_count === 0).length}</div>
                </div>
              </div>

              <div className="space-y-3">
                {docs.map((d) => {
                  const isIngested = d.chunk_count > 0;
                  const isApproved = d.status === "approved";
                  const isArchived = d.status === "archived";
                  const isLive = isApproved && isIngested;

                  const steps = [
                    { label: "Uploaded", done: true, icon: UploadCloud },
                    { label: "Ingested", done: isIngested, icon: isIngested ? CheckCircle : AlertCircle, count: d.chunk_count },
                    { label: "Approved", done: isApproved, icon: isApproved ? CheckCircle2 : AlertCircle },
                    { label: "Live", done: isLive, icon: isLive ? BookOpen : XCircle },
                  ];

                  return (
                    <div key={d.id} className={`flex flex-col gap-3 p-4 border rounded-lg ${isArchived ? "opacity-60 bg-muted/30" : ""}`}>
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-medium">{d.title}</div>
                            <Badge variant={isApproved ? "default" : isArchived ? "secondary" : "outline"}>{d.status}</Badge>
                            {isApproved && !isIngested && (
                              <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" /> Approved but no chunks</Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground flex flex-wrap gap-2 mt-1">
                            <Badge variant="outline">{d.organisation}</Badge>
                            <Badge variant="outline">{d.jurisdiction}</Badge>
                            <Badge variant="outline">{d.evidence_level.replace("_", " ")}</Badge>
                            {d.version && <Badge variant="outline">v{d.version}</Badge>}
                            <Badge variant={d.approved_for_rag ? "default" : "outline"} className="gap-1">
                              {d.approved_for_rag ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                              RAG {d.approved_for_rag ? "on" : "off"}
                            </Badge>
                            <Badge variant={d.copyright_status && d.copyright_status !== "unknown" ? "outline" : "destructive"} className="gap-1">
                              {(!d.copyright_status || d.copyright_status === "unknown") && <AlertCircle className="h-3 w-3" />}
                              copyright: {d.copyright_status ?? "unknown"}
                            </Badge>
                            {d.next_review_at && new Date(d.next_review_at) < new Date() && (
                              <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" /> Review overdue</Badge>
                            )}
                            <span>Reviewed: {d.reviewed_at}</span>
                            <span>Next review: {d.next_review_at}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => openEdit(d)}>
                            <Pencil className="h-3 w-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openReview(d)}>
                            <Eye className="h-3 w-3 mr-1" /> Review chunks
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => ingest(d.id)} disabled={ingestingId === d.id}>
                            {ingestingId === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                            <span className="ml-1">Ingest</span>
                          </Button>
                          {!isApproved && !isArchived && (
                            <Button size="sm" onClick={() => setStatus(d.id, "approved")}>
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                            </Button>
                          )}
                          {!isArchived && (
                            <Button size="sm" variant="secondary" onClick={() => setStatus(d.id, "archived")}>
                              <Archive className="h-3 w-3 mr-1" /> Archive
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-1">
                        {steps.map((s, idx) => (
                          <div key={s.label} className="flex items-center gap-2">
                            <div className={`flex items-center gap-1.5 text-sm px-2 py-1 rounded-full border ${s.done ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground border-muted"}`}>
                              <s.icon className="h-3.5 w-3.5" />
                              <span>{s.label}</span>
                              {s.count !== undefined && s.done && <span className="text-xs font-medium opacity-90">({s.count})</span>}
                            </div>
                            {idx < steps.length - 1 && <div className={`hidden md:block w-6 h-px ${steps[idx + 1].done ? "bg-primary" : "bg-muted"}`} />}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit metadata dialog */}
      <Dialog open={!!editDoc} onOpenChange={(open) => !open && setEditDoc(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit metadata</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2 py-4">
            <div className="md:col-span-2">
              <Label>Title</Label>
              <Input value={editForm.title ?? ""} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
            </div>
            <div>
              <Label>Organisation</Label>
              <Select value={editForm.organisation ?? ORGS[0]} onValueChange={(v) => setEditForm({ ...editForm, organisation: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ORGS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jurisdiction</Label>
              <Select value={editForm.jurisdiction ?? "AU"} onValueChange={(v) => setEditForm({ ...editForm, jurisdiction: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{JURIS.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Evidence Level</Label>
              <Select value={editForm.evidence_level ?? "guideline"} onValueChange={(v) => setEditForm({ ...editForm, evidence_level: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Version</Label>
              <Input value={editForm.version ?? ""} onChange={(e) => setEditForm({ ...editForm, version: e.target.value })} />
            </div>
            <div>
              <Label>Published Date</Label>
              <Input type="date" value={editForm.published_at ?? ""} onChange={(e) => setEditForm({ ...editForm, published_at: e.target.value })} />
            </div>
            <div>
              <Label>Reviewed Date</Label>
              <Input type="date" value={editForm.reviewed_at ?? ""} onChange={(e) => setEditForm({ ...editForm, reviewed_at: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Source URL</Label>
              <Input value={editForm.source_url ?? ""} onChange={(e) => setEditForm({ ...editForm, source_url: e.target.value })} />
            </div>
          </div>

          {/* Copyright & permission panel */}
          <div className="border-t pt-4 mt-2">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold">Copyright &amp; permission</h3>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Document type</Label>
                <Select value={editForm.document_type ?? "guideline"} onValueChange={(v) => setEditForm({ ...editForm, document_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Country</Label>
                <Input value={editForm.country ?? ""} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} placeholder="AU, UK, US…" />
              </div>
              <div>
                <Label>Source version</Label>
                <Input value={editForm.source_version ?? ""} onChange={(e) => setEditForm({ ...editForm, source_version: e.target.value })} placeholder="e.g. 2024 update" />
              </div>
              <div>
                <Label>Last checked date</Label>
                <Input type="date" value={editForm.last_checked_date ?? ""} onChange={(e) => setEditForm({ ...editForm, last_checked_date: e.target.value })} />
              </div>
              <div>
                <Label>Copyright status</Label>
                <Select value={editForm.copyright_status ?? "unknown"} onValueChange={(v) => setEditForm({ ...editForm, copyright_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{COPYRIGHT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Permission status</Label>
                <Select value={editForm.permission_status ?? "not_checked"} onValueChange={(v) => setEditForm({ ...editForm, permission_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PERMISSION_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Usage restrictions</Label>
                <Textarea rows={2} value={editForm.usage_restrictions ?? ""} onChange={(e) => setEditForm({ ...editForm, usage_restrictions: e.target.value })} placeholder="Any limits on how this source may be used (e.g. no verbatim reproduction, non-commercial only)." />
              </div>
              <div>
                <Label>Reviewer name</Label>
                <Input value={editForm.reviewer_name ?? ""} onChange={(e) => setEditForm({ ...editForm, reviewer_name: e.target.value })} placeholder="Who checked copyright?" />
              </div>
              <div className="md:col-span-2">
                <Label>Review notes</Label>
                <Textarea rows={2} value={editForm.review_notes ?? ""} onChange={(e) => setEditForm({ ...editForm, review_notes: e.target.value })} />
              </div>
            </div>

            <div className="mt-4 space-y-3 rounded-lg border p-3 bg-muted/30">
              <div className="text-sm font-medium">Approval toggles</div>
              <p className="text-xs text-muted-foreground">
                All toggles default to off. A reviewer name and notes are required before enabling. Turning off RAG removes this document from Aura retrieval immediately.
              </p>
              {[
                { key: "approved_for_rag" as const, label: "Approved for RAG (evidence layer)" },
                { key: "approved_for_quotation" as const, label: "Approved for short quotation" },
                { key: "approved_for_reproduction" as const, label: "Approved for reproduction" },
              ].map(({ key, label }) => {
                const canEnable = !!(editForm.reviewer_name && editForm.review_notes);
                const checked = !!editForm[key];
                return (
                  <div key={key} className="flex items-center justify-between gap-3">
                    <Label htmlFor={key} className="text-sm">{label}</Label>
                    <Switch
                      id={key}
                      checked={checked}
                      disabled={!checked && !canEnable}
                      onCheckedChange={(v) => setEditForm({ ...editForm, [key]: v })}
                    />
                  </div>
                );
              })}
              {!(editForm.reviewer_name && editForm.review_notes) && (
                <div className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Add reviewer name + notes to enable approvals.</div>
              )}
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button onClick={saveEdit}>Save metadata</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review chunks dialog */}
      <Dialog open={!!reviewDoc} onOpenChange={(open) => !open && setReviewDoc(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Review chunks — {reviewDoc?.title}</DialogTitle>
          </DialogHeader>
          {chunksLoading ? (
            <Loader2 className="h-6 w-6 animate-spin m-4" />
          ) : chunks.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No chunks yet. Ingest the document first.</p>
          ) : (
            <>
              <div className="text-sm text-muted-foreground mb-2">{chunks.length} chunks. Toggle excluded to remove headers, footers, OCR noise, or empty paragraphs.</div>
              <ScrollArea className="flex-1 pr-2">
                <div className="space-y-3">
                  {chunks.map((c) => (
                    <div key={c.id} className={`p-3 border rounded-lg ${c.excluded ? "opacity-50 bg-muted" : ""}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-xs text-muted-foreground">
                          #{c.chunk_index} · {c.heading_path || "No heading"} · p.{c.page_start ?? "?"}{c.page_end && c.page_end !== c.page_start ? `–${c.page_end}` : ""} · {c.token_count ?? "?"} tokens
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Label htmlFor={`exclude-${c.id}`} className="text-xs">Exclude</Label>
                          <Switch id={`exclude-${c.id}`} checked={c.excluded} onCheckedChange={() => toggleExcluded(c)} />
                        </div>
                      </div>
                      <Textarea readOnly value={c.content} className="mt-2 text-sm min-h-[80px] resize-none" />
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
